// src/pages/AssemblyDetail.jsx
import React, { useEffect, useState, useContext } from 'react';
import { ConfigContext, http } from '../../base-ui/lib';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Box,
	Grid,
	Paper,
	Typography,
	IconButton,
	TextField,
	Divider,
	MenuItem,
	Button,
	Snackbar,
	Alert,
	Select,
	InputLabel,
	FormControl,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { toastError, toastSuccess } from '../../Toaster/Toast';

export default function AssemblyDetail() {
	const [hasErrored, setHasErrored] = useState(false);
	const [formData, setFormData] = useState({
		name: '',
		assemblyTypeId: '',
		edgeTypeId: '',
		materials: [],
		limiters: [], // Array of { limiterId, value }
	});
	const [searchQuery, setSearchQuery] = useState('');
	const [searchResults, setSearchResults] = useState({});
	const [assemblyTypes, setAssemblyTypes] = useState([]);
	const [edgeTypes, setEdgeTypes] = useState([]); // New state for edge types
	const [limiters, setLimiters] = useState([]); // Array of Limiter objects
	const { config } = useContext(ConfigContext);
	const { assemblyId } = useParams();
	const navigate = useNavigate();

	// State for validation errors
	const [errors, setErrors] = useState({
		name: '',
		assemblyTypeId: '',
		edgeTypeId: '',
		// Dynamic keys for limiters, e.g., limiter_<id>
	});

	// State for Snackbar notifications
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: '',
		severity: 'success', // 'success' | 'error' | 'warning' | 'info'
	});

	// Fetch assembly, materials, assembly types, and limiters on component mount
	useEffect(() => {
		const fetchData = async () => {
			if (assemblyId === 'add') {
				// Handle "add" scenario
				// Initialize form data for adding a new assembly
				setFormData({
					name: '',
					assemblyTypeId: '',
					edgeTypeId: '',
					materials: [],
					limiters: [],
				});
				try {
					// Fetch all materials
					const materialsResponse = await http.get(
						{ pageSize: 1000, pageOffset: 0, searchQuery: '' },
						`${config.uri.base}/material`
					);
					if (!materialsResponse.errored) {
						const groupedByCategory = materialsResponse.rows
							.filter((material) => !material.archive) // Exclude archived items
							.reduce((acc, material) => {
								const { category } = material;
								if (!acc[category]) acc[category] = [];
								acc[category].push(material);
								return acc;
							}, {});
						setSearchResults(groupedByCategory);
					} else {
						setSnackbar({
							open: true,
							message: 'Error fetching materials.',
							severity: 'error',
						});
					}

					// Fetch assembly types from /assemblyType
					const typesResponse = await http.get(
						{ pageSize: 1000, pageOffset: 0 },
						`${config.uri.base}/assemblyType`
					);
					if (!typesResponse.errored) {
						setAssemblyTypes(typesResponse.rows);
					} else {
						setSnackbar({
							open: true,
							message: 'Error fetching assembly types.',
							severity: 'error',
						});
					}

					// Fetch edge types from /edgeType
					const edgeTypeResponse = await http.get(
						{ pageSize: 50, pageOffset: 0 },
						`${config.uri.base}/edgeType`
					); // Fetch edge types
					if (!edgeTypeResponse.errored) {
						setEdgeTypes(edgeTypeResponse.rows);
					} else {
						setSnackbar({
							open: true,
							message: 'Error fetching edge types.',
							severity: 'error',
						});
					}
				} catch (error) {
					console.error('Error fetching data:', error);
					setHasErrored(true);
				}
				return; // Exit early since there's no assembly to fetch
			}

			// Existing behavior for fetching an existing assembly
			try {
				// Fetch assembly by ID
				const assemblyResponse = await http.get(
					{},
					`${config.uri.base}/assembly/${assemblyId}`
				);
				if (assemblyResponse.errored) {
					setHasErrored(true);
					setSnackbar({
						open: true,
						message: 'Assembly not found.',
						severity: 'error',
					});
					return;
				}

				setFormData(assemblyResponse);

				// Fetch all materials
				const materialsResponse = await http.get(
					{ pageSize: 1000, pageOffset: 0, searchQuery: '' },
					`${config.uri.base}/material`
				);
				if (!materialsResponse.errored) {
					const groupedByCategory = materialsResponse.rows
						.filter((material) => !material.archive) // Exclude archived items
						.reduce((acc, material) => {
							const { category } = material;
							if (!acc[category]) acc[category] = [];
							acc[category].push(material);
							return acc;
						}, {});
					setSearchResults(groupedByCategory);
				} else {
					setSnackbar({
						open: true,
						message: 'Error fetching materials.',
						severity: 'error',
					});
				}

				// Fetch edge types from /edgeType
				const edgeTypesResponse = await http.get(
					{ pageSize: 50, pageOffset: 0 },
					`${config.uri.base}/edgeType`
				); // Fetch edge types
				if (!edgeTypesResponse.errored) {
					setEdgeTypes(edgeTypesResponse.rows);
				} else {
					setSnackbar({
						open: true,
						message: 'Error fetching edge types.',
						severity: 'error',
					});
				}

				// Fetch assembly types from /assemblyType
				const typesResponse = await http.get(
					{ pageSize: 100, pageOffset: 0 },
					`${config.uri.base}/assemblyType`
				);
				if (!typesResponse.errored) {
					setAssemblyTypes(typesResponse.rows);
				} else {
					setSnackbar({
						open: true,
						message: 'Error fetching assembly types.',
						severity: 'error',
					});
				}

				// Fetch Limiters associated with the assembly's assemblyTypeId
				const assemblyTypeResponse = await http.get(
					{},
					`${config.uri.base}/assemblyType/${assemblyResponse.assemblyTypeId}`
				);
				if (
					!assemblyTypeResponse.errored &&
					assemblyTypeResponse.limiterDetails
				) {
					setLimiters(assemblyTypeResponse.limiterDetails);
				} else {
					setSnackbar({
						open: true,
						message: 'No limiters have been assigned to this Assembly Type.',
						severity: 'warning',
					});
				}
			} catch (error) {
				console.error('Error fetching data:', error);
				setHasErrored(true);
				setSnackbar({
					open: true,
					message: 'Error fetching assembly details.',
					severity: 'error',
				});
			}
		};

		fetchData();
	}, [assemblyId, config.uri.base]);

	// Fetch Limiters when assemblyTypeId changes
	useEffect(() => {
		const fetchLimitersForAssemblyType = async () => {
			if (!formData.assemblyTypeId) {
				setLimiters([]);
				setFormData((prevFormData) => ({
					...prevFormData,
					limiters: [],
				}));
				return;
			}

			try {
				const assemblyTypeResponse = await http.get(
					{},
					`${config.uri.base}/assemblyType/${formData.assemblyTypeId}`
				);
				if (!assemblyTypeResponse.errored && assemblyTypeResponse.limiters) {
					setLimiters(assemblyTypeResponse.limiterDetails);

					// Initialize limiter values if not present
					const updatedLimiters = assemblyTypeResponse.limiterDetails.map(
						(limiter) => {
							const existingLimiter = formData.limiters.find(
								(l) => l._id === limiter._id
							);
							return existingLimiter
								? existingLimiter
								: {
										_id: limiter._id,
										value: limiter.type === 'numeric' ? 0 : '',
									};
						}
					);
					setFormData((prevFormData) => ({
						...prevFormData,
						limiters: updatedLimiters,
					}));
				} else {
					setSnackbar({
						open: true,
						message: 'No limiters have been assigned to this Assembly Type.',
						severity: 'warning',
					});
				}
			} catch (error) {
				console.error('Error fetching limiters:', error);
				setSnackbar({
					open: true,
					message: 'Error fetching limiters for the selected Assembly Type.',
					severity: 'error',
				});
			}
		};

		fetchLimitersForAssemblyType();
	}, [formData.assemblyTypeId]);

	const handleAddMaterial = (material) => {
		// Check if the material is already added to prevent duplicates
		const isAlreadyAdded = formData.materials.some(
			(m) => m._id === material._id
		);

		if (!isAlreadyAdded) {
			setFormData({
				...formData,
				materials: [
					...formData.materials,
					{
						_id: material._id,
						obid: material.obid,
						archive: material.archive,
						reface: material.reface,
						category: material.category,
						qty: 1,
						price: material.price,
						name: material.name,
					},
				],
			});
		} else {
			setSnackbar({
				open: true,
				message: 'Material already added.',
				severity: 'warning',
			});
		}
	};

	const handleSearchChange = (e) => {
		setSearchQuery(e.target.value);
	};

	const handleLimiterChange = ({ _id, name, type, value }) => {
		//Find existing limiter if exists and then update it
		const existingLimiter = formData?.limiters?.find((l) => l._id === _id);
		if (existingLimiter) {
			const updatedLimiters = formData.limiters.map((lim) => {
				if (lim._id === _id) {
					if (
						type === 'numeric' &&
						(value === '' || value === null || isNaN(value))
					) {
						return { ...lim, name, type, value: 0 };
					} else if (type === 'numeric') {
						return { ...lim, name, type, value: parseFloat(value) };
					}
					return { ...lim, name, type, value };
				}
				return lim;
			});
			setFormData({ ...formData, limiters: updatedLimiters });
			return;
		} else {
			// If limiter is not found, add it to the List
			const lim = limiters.find((l) => l._id === _id);
			if (lim) {
				setFormData({
					...formData,
					limiters: [
						...(formData.limiters ? formData.limiters : []),
						{ ...lim, name, type, value },
					],
				});
			}
		}
	};

	const handleSave = async () => {
		try {
			// Reset previous errors
			setErrors({ name: '', assemblyTypeId: '', edgeTypeId: '' });

			// Validate formData before saving
			let validationErrors = {};
			if (!formData.name.trim()) {
				validationErrors.name = 'Name is required.';
			}
			if (!formData.assemblyTypeId) {
				validationErrors.assemblyTypeId = 'Assembly Type is required.';
			}
			if (!formData.edgeTypeId) {
				validationErrors.edgeTypeId = 'Edge Type is required.';
			}

			// Validate limiters
			if (limiters.length > 0) {
				formData.limiters.forEach((limiter) => {
					const lim = limiters.find((l) => l._id === limiter._id);
					if (lim.type === 'numeric') {
						if (
							limiter.value === '' ||
							limiter.value === null ||
							isNaN(limiter.value)
						) {
							validationErrors[`limiter_${limiter._id}`] =
								`${lim.name} value is required and must be a number.`;
						}
					}
					if (lim.type === 'dropdown' && !limiter.value) {
						validationErrors[`limiter_${limiter._id}`] =
							`${lim.name} selection is required.`;
					}
				});
			}

			if (Object.keys(validationErrors).length > 0) {
				setErrors(validationErrors);
				setSnackbar({
					open: true,
					message: 'Please fix the errors before saving.',
					severity: 'error',
				});
				return;
			}

			// Prepare the data to send to the API
			const payload = {
				name: formData.name,
				assemblyTypeId: formData.assemblyTypeId,
				edgeTypeId: formData.edgeTypeId,
				length: formData.length,
				materials: formData.materials,
				limiters: formData.limiters,
			};

			if (assemblyId === 'add') {
				// Determine if adding a new assembly
				const response = await http.post(
					payload,
					`${config.uri.base}/assembly`
				);
				if (response.errored)
					return toastError({
						title: response.error.message,
					});

				toastSuccess({
					title: 'Assembly added successfully!',
				});
				navigate('/assembly'); // Redirect to /assembly after a short delay
			} else {
				// Updating an existing assembly
				const response = await http.put(
					payload,
					`${config.uri.base}/assembly/${assemblyId}`
				);
				if (response.errored)
					return toastError({
						title: response.error.message,
					});

				toastSuccess({
					title: 'Assembly updated successfully!',
				});
			}
		} catch (error) {
			console.error(
				`Error ${assemblyId === 'add' ? 'adding' : 'updating'} assembly:`,
				error
			);
			toastError({
				title: `An error occurred while ${assemblyId === 'add' ? 'adding' : 'updating'} the assembly.`,
			});
		}
	};

	// Close the Snackbar
	const handleCloseSnackbar = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		setSnackbar({ ...snackbar, open: false });
	};

	// Filter materials based on search query
	const filteredMaterials = (materials) => {
		return materials.filter(
			(material) =>
				(material.name &&
					material.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
				(material.obid &&
					material.obid.toLowerCase().includes(searchQuery.toLowerCase()))
		);
	};

	// Calculate total price for added materials
	const calculateTotalPrice = () => {
		return formData.materials.reduce(
			(acc, material) => acc + material.qty * material.price,
			0
		);
	};

	return (
		<React.Fragment>
			{/* Snackbar for notifications */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					sx={{ width: '100%' }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>

			{hasErrored ? (
				<Box sx={{ textAlign: 'center', marginTop: 4 }}>
					<Typography variant="h4" color="error">
						Assembly not found
					</Typography>
				</Box>
			) : (
				<Grid container spacing={2}>
					{/* Left Hand Side - Form Fields and Material List */}
					<Grid item xs={12} sm={6}>
						<Box>
							{/* Form Fields Aligned in a Single Row */}
							<Grid container spacing={2}>
								{/* Name Field */}
								<Grid item xs={12} sm={4}>
									<TextField
										label="Name"
										value={formData.name}
										onChange={(e) =>
											setFormData({ ...formData, name: e.target.value })
										}
										fullWidth
										required
										error={Boolean(errors.name)}
										helperText={errors.name}
									/>
								</Grid>

								<Grid item xs={12} sm={4}>
									<FormControl
										fullWidth
										variant="outlined"
										required
										error={Boolean(errors.edgeTypeId)}
									>
										<InputLabel id="edge-type-label">Edge Type</InputLabel>
										<Select
											labelId="edge-type-label"
											label="Edge Type"
											value={formData.edgeTypeId}
											onChange={(e) =>
												setFormData({ ...formData, edgeTypeId: e.target.value })
											}
										>
											{edgeTypes.map((type) => (
												<MenuItem key={type._id} value={type._id}>
													{type.name}
												</MenuItem>
											))}
										</Select>
										{errors.edgeTypeId && (
											<Typography variant="caption" color="error">
												{errors.edgeTypeId}
											</Typography>
										)}
									</FormControl>
								</Grid>

								{/* Assembly Type Select */}
								<Grid item xs={12} sm={4}>
									<FormControl
										fullWidth
										variant="outlined"
										required
										error={Boolean(errors.assemblyTypeId)}
									>
										<InputLabel id="assembly-type-label">
											Assembly Type
										</InputLabel>
										<Select
											labelId="assembly-type-label"
											label="Assembly Type"
											value={formData.assemblyTypeId}
											onChange={(e) =>
												setFormData({
													...formData,
													assemblyTypeId: e.target.value,
												})
											}
										>
											{assemblyTypes.map((type) => (
												<MenuItem key={type._id} value={type._id}>
													{type.name}
												</MenuItem>
											))}
										</Select>
										{errors.assemblyTypeId && (
											<Typography variant="caption" color="error">
												{errors.assemblyTypeId}
											</Typography>
										)}
									</FormControl>
								</Grid>
							</Grid>

							{/* Limiters Section */}
							{limiters.length > 0 && (
								<Box sx={{ marginTop: 3 }}>
									<Typography variant="h6">Limiters</Typography>
									<Divider sx={{ marginY: 1 }} />
									{limiters.map((limiter) => (
										<Box key={limiter._id} sx={{ marginBottom: 2 }}>
											<Typography variant="subtitle1">
												{limiter.name}
											</Typography>
											{limiter.type === 'dropdown' ? (
												<FormControl
													fullWidth
													variant="outlined"
													required
													error={Boolean(errors[`limiter_${limiter._id}`])}
												>
													<InputLabel id={`limiter-${limiter._id}-label`}>
														{limiter.name}
													</InputLabel>
													<Select
														labelId={`limiter-${limiter._id}-label`}
														label={limiter.name}
														value={
															formData?.limiters?.find(
																(l) => l._id === limiter._id
															)?.value || ''
														}
														onChange={(e) =>
															handleLimiterChange({
																_id: limiter._id,
																name: limiter.name,
																type: limiter.type,
																value: e.target.value,
															})
														}
													>
														{limiter.values &&
															limiter.values.map((val, idx) => (
																<MenuItem key={idx} value={val}>
																	{val}
																</MenuItem>
															))}
													</Select>
													{errors[`limiter_${limiter._id}`] && (
														<Typography variant="caption" color="error">
															{errors[`limiter_${limiter._id}`]}
														</Typography>
													)}
												</FormControl>
											) : (
												<TextField
													label={limiter.name}
													type="number"
													variant="outlined"
													fullWidth
													required
													value={
														formData?.limiters?.find(
															(l) => l._id === limiter._id
														)?.value || ''
													}
													onChange={(e) =>
														handleLimiterChange({
															_id: limiter._id,
															name: limiter.name,
															type: limiter.type,
															value: e.target.value,
														})
													}
													error={Boolean(errors[`limiter_${limiter._id}`])}
													helperText={errors[`limiter_${limiter._id}`]}
												/>
											)}
										</Box>
									))}
								</Box>
							)}

							<Typography variant="h6" sx={{ marginTop: 3 }}>
								Materials
							</Typography>

							{/* Search input field */}
							<TextField
								label="Search Materials"
								value={searchQuery}
								onChange={handleSearchChange}
								fullWidth
								variant="outlined"
								sx={{ marginBottom: 2 }}
							/>

							{/* Scrollable Materials List */}
							<Box
								sx={{
									maxHeight: '75vh', // Adjusted height for better layout
									overflowY: 'auto',
									paddingRight: 1,
								}}
							>
								{Object.keys(searchResults).length === 0 ? (
									<Typography variant="body2" color="textSecondary">
										No materials available.
									</Typography>
								) : (
									Object.keys(searchResults).map((category) => (
										<Box key={category} sx={{ marginTop: 2 }}>
											{/* Category Container */}
											<Paper
												elevation={3}
												sx={{ padding: 1, backgroundColor: '#fafafa' }}
											>
												<Typography variant="subtitle1" gutterBottom>
													{category}
												</Typography>
												<Grid container spacing={1}>
													{filteredMaterials(searchResults[category]).length ===
													0 ? (
														<Grid item xs={12}>
															<Typography variant="body2" color="textSecondary">
																No materials found in this category.
															</Typography>
														</Grid>
													) : (
														filteredMaterials(searchResults[category]).map(
															(material) => (
																<Grid item xs={6} md={4} key={material._id}>
																	<Paper
																		onClick={() => handleAddMaterial(material)}
																		sx={{
																			padding: 0.5,
																			cursor: 'pointer',
																			textAlign: 'left',
																			fontSize: '0.75rem',
																			height: '40px',
																			display: 'flex',
																			alignItems: 'center',
																			bgcolor: '#f5f5f5',
																			'&:hover': {
																				backgroundColor: '#e0e0e0',
																			},
																		}}
																	>
																		<Typography
																			variant="body2"
																			sx={{ flexGrow: 1 }}
																		>
																			{material.name}
																		</Typography>
																		<Typography
																			variant="caption"
																			sx={{ whiteSpace: 'nowrap' }}
																		>
																			${material.price}
																		</Typography>
																	</Paper>
																</Grid>
															)
														)
													)}
												</Grid>
											</Paper>
											<Divider sx={{ marginTop: 1, marginBottom: 1 }} />
										</Box>
									))
								)}
							</Box>
						</Box>
					</Grid>

					{/* Right Hand Side - Summary, Save Button, and Added Materials */}
					<Grid item xs={12} sm={6}>
						<Box>
							{/* Summary and Save Button Section */}
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									marginBottom: 3,
								}}
							>
								{/* Summary Section */}
								<Box>
									<Typography variant="h6">Summary</Typography>
									<Typography variant="body1">
										Total Price: ${calculateTotalPrice().toFixed(2)}
									</Typography>
								</Box>

								{/* Save Button */}
								<Button
									variant="contained"
									color="primary"
									onClick={handleSave}
									sx={{ height: 'fit-content' }}
								>
									Save Assembly
								</Button>
							</Box>

							<Typography variant="h6">Added Materials</Typography>

							{/* Scrollable Added Materials List */}
							<Box
								sx={{
									maxHeight: '82vh', // Adjust as needed
									overflowY: 'auto',
									paddingRight: 1,
									marginTop: 2,
									marginBottom: 2,
								}}
							>
								{formData.materials.length === 0 ? (
									<Typography variant="body2" color="textSecondary">
										No materials added.
									</Typography>
								) : (
									formData.materials.map((material, index) => {
										return (
											<Paper sx={{ padding: 0.5, marginTop: 1 }} key={index}>
												<Box display="flex" alignItems="center" height="40px">
													<Box
														sx={{
															flexGrow: 1,
															display: 'flex',
															alignItems: 'center',
														}}
													>
														<Typography variant="body2" sx={{ marginRight: 2 }}>
															{material.name}
														</Typography>
													</Box>
													<Box sx={{ display: 'flex', alignItems: 'center' }}>
														<Typography variant="body2" sx={{ marginRight: 2 }}>
															${(material.qty * material.price).toFixed(2)}
														</Typography>
														<TextField
															label="Qty"
															type="number"
															value={material.qty}
															onChange={(e) => {
																const updatedMaterials = [
																	...formData.materials,
																];
																updatedMaterials[index].qty = e.target.value;
																setFormData({
																	...formData,
																	materials: updatedMaterials,
																});
															}}
															sx={{ width: '100px', marginRight: 1 }}
															size="small"
															InputProps={{
																inputProps: { min: 0.1, step: 0.01 },
															}}
														/>
														<IconButton
															onClick={() => {
																const updatedMaterials = [
																	...formData.materials,
																];
																updatedMaterials.splice(index, 1);
																setFormData({
																	...formData,
																	materials: updatedMaterials,
																});
															}}
															size="small"
														>
															<Delete fontSize="small" />
														</IconButton>
													</Box>
												</Box>
											</Paper>
										);
									})
								)}
							</Box>
						</Box>
					</Grid>
				</Grid>
			)}
		</React.Fragment>
	);
}
